import { AxiosResponse } from 'axios'

import { CURRENCY_TO_REGION_ID } from '@src/config/localisation/locale-config.constants'
import { FALLBACK_REGION_ID } from '@src/config/localisation/locale-config.static-constants'
import { REGION_ID_HEADER } from '@src/constants/api.headers.constants'
import { isB2BSelfServeContext } from '@src/utils/b2b/b2b.utils'
import { getStorage } from '@src/utils/enhancedStorage'
import { keyBy, omit } from '@src/utils/functions/utils'
import { getLocalisationHeaders } from '@src/utils/general.utils'

import isBrowser from '../../utils/isBrowser'
import {
  CART_AUTH_CODE_KEY,
  CART_AUTH_CODE_RES_KEY,
  LOCAL_STORAGE_CART_OBJ_KEY,
  LOCAL_STORAGE_NEW_CART_ID_KEY,
} from '../checkoutService/new-checkout.constants'
import { getInventory } from '../inventoryService/inventory.service'

import { getProductData } from './cart.service'
import { Cart, CartLineItem, SimplifiedVariant } from './cart.service.types'

export function setCartAuthCodeFromHeader(res: AxiosResponse) {
  const cartAuthCode = res.headers[CART_AUTH_CODE_RES_KEY] as string
  if (cartAuthCode) {
    getStorage().setItem(CART_AUTH_CODE_KEY, cartAuthCode, { isGlobal: false })
  }
}

export function getCartAuthCodeFromHeader() {
  if (!isBrowser()) return null

  const code = getStorage().getItem(CART_AUTH_CODE_KEY, { isGlobal: false })
  return code
}

export async function getInventoryDataForCart(data: Cart) {
  if (
    data.lineItems?.length === 0 &&
    (!data.saveForLaterItems?.length || data.saveForLaterItems.length === 0) &&
    (!data.outOfStockLineItems?.length || data.outOfStockLineItems.length === 0)
  )
    return {}

  const variantIds = data.lineItems?.map((item: any) => item.variantId) ?? []
  const saveForLaterVariantIds = data.saveForLaterItems?.map((item: any) => item.variantId) ?? []
  const outOfStockVariantIds = data.outOfStockLineItems?.map((item: any) => item.variantId) ?? []
  const allVariantIds = [...variantIds, ...saveForLaterVariantIds, ...outOfStockVariantIds].join(',')
  const inventoryData = await getInventory({ variantIds: allVariantIds })
  return inventoryData
}

export async function getProductDataForCart(data: Cart) {
  if (!isBrowser()) return {}
  const productIdMap =
    data?.lineItems?.reduce((acc: any, lineItem: CartLineItem) => {
      return {
        ...acc,
        [lineItem.variantId]: lineItem?.variantMetaData?.productId,
      }
    }, {}) || {}

  const saveForLaterProductIdMap =
    data?.saveForLaterItems?.reduce((acc: any, lineItem: CartLineItem) => {
      return {
        ...acc,
        [lineItem.variantId]: lineItem?.variantMetaData?.productId,
      }
    }, {}) || {}

  const outOfStockProductIdMap =
    data?.outOfStockLineItems?.reduce((acc: any, lineItem: CartLineItem) => {
      return {
        ...acc,
        [lineItem.variantId]: lineItem?.variantMetaData?.productId,
      }
    }, {}) || {}

  const totalProductIdMap = { ...productIdMap, ...saveForLaterProductIdMap, ...outOfStockProductIdMap }

  const productIds = Object.values(totalProductIdMap)
  const products =
    productIds.length > 0
      ? await getProductData({
          productIds,
          useBatching: true,
        })
      : {}
  const productMap = keyBy(products, 'id')

  const productDataWithVariantMap = Object.entries(totalProductIdMap).reduce(
    (acc: any, [variantId, productId]: any) => {
      return {
        ...acc,
        [variantId]: {
          productData: omit(productMap[productId], [`variants`]),
          selectedVariant: productMap[productId]?.variants?.find((variant: any) => {
            return variant?.id?.toString() === variantId
          }),
        },
      }
    },
    {},
  )
  return productDataWithVariantMap
}

export async function getProductDataForCartWithAllVariants(data: Cart) {
  if (!isBrowser()) return { productDataMap: {}, allVariantIds: [] }

  const productIdMap =
    data?.lineItems?.reduce((acc: Record<string, string | number>, lineItem: CartLineItem) => {
      return {
        ...acc,
        [lineItem.variantId]: lineItem?.variantMetaData?.productId,
      }
    }, {}) ?? {}

  const saveForLaterProductIdMap =
    data?.saveForLaterItems?.reduce((acc: Record<string, string | number>, lineItem: CartLineItem) => {
      return {
        ...acc,
        [lineItem.variantId]: lineItem?.variantMetaData?.productId,
      }
    }, {}) ?? {}

  const outOfStockProductIdMap =
    data?.outOfStockLineItems?.reduce((acc: Record<string, string | number>, lineItem: CartLineItem) => {
      return {
        ...acc,
        [lineItem.variantId]: lineItem?.variantMetaData?.productId,
      }
    }, {}) ?? {}

  const totalProductIdMap: Record<string, string | number> = {
    ...productIdMap,
    ...saveForLaterProductIdMap,
    ...outOfStockProductIdMap,
  }

  const productIds = Object.values(totalProductIdMap)
  if (productIds.length === 0) return { productDataMap: {}, allVariantIds: [] }

  const products = await getProductData({
    productIds,
    useBatching: true,
  })
  const productMap: Record<string, Record<string, unknown>> = keyBy(products, 'id')

  const productDataMap = Object.entries(totalProductIdMap).reduce(
    (
      acc: Record<string, { productData: Record<string, unknown>; selectedVariant: unknown }>,
      [variantId, productId],
    ) => {
      return {
        ...acc,
        [variantId]: {
          productData: omit(productMap[productId as string], [`variants`]),
          selectedVariant: (productMap[productId as string]?.variants as Record<string, unknown>[] | undefined)?.find(
            variant => {
              return variant?.id?.toString() === variantId
            },
          ),
        },
      }
    },
    {},
  )

  const allVariantIdSet = new Set<string>()
  const allProductVariants: Record<string, SimplifiedVariant[]> = {}
  Object.entries(productMap).forEach(([productId, product]) => {
    const variants = ((product?.variants as Record<string, unknown>[]) ?? []).map(v => {
      if (v?.id != null) {
        allVariantIdSet.add(String(v.id))
      }
      // Per catalog contract: variants that support customization include a
      // `customization` object; variants that do NOT support it omit the field
      // entirely. Use field-presence (Object.hasOwn) — do NOT compare to null.
      // eslint-disable-next-line i18next/no-literal-string -- object key, not user-visible text
      const catalogOffersCustomization = Object.prototype.hasOwnProperty.call(v ?? {}, 'customization')
      return {
        id: typeof v.id === 'number' ? v.id : Number(v.id),
        title: typeof v.title === 'string' ? v.title : '',
        price: typeof v.price === 'number' ? v.price : Number(v.price) || 0,
        options: Array.isArray(v.options)
          ? (v.options as Record<string, unknown>[]).map(o => ({
              name: typeof o.name === 'string' ? o.name : '',
              value: typeof o.value === 'string' ? o.value : '',
            }))
          : [],
        catalogOffersCustomization,
      }
    })
    allProductVariants[productId] = variants
  })

  return { productDataMap, allVariantIds: Array.from(allVariantIdSet), allProductVariants }
}

export function mergeInventoryDataInCart(data: Cart, inventoryData: any) {
  const lineItems = data.lineItems.map(item => {
    return {
      ...item,
      inventoryData: inventoryData[item.variantId],
    }
  })

  return {
    ...data,
    lineItems,
  }
}

function mergeProductDataInCart(data: any, _productData: any) {
  const lineItems = data.lineItems.map((lineItem: any) => {
    const variantId = lineItem.variantId
    const product = _productData[variantId]
    const productMetaData = product.selectedVariant
    const slug = product.productData.slug

    return {
      ...lineItem,
      productMetaData,
      slug,
    }
  })

  const saveForLaterlineItems =
    data?.saveForLaterItems?.map((lineItem: any) => {
      const variantId = lineItem.variantId
      const product = _productData[variantId]
      const productMetaData = product?.selectedVariant
      const slug = product?.productData?.slug

      return {
        ...lineItem,
        productMetaData,
        slug,
      }
    }) || []

  const outOfStocklineItems =
    data?.outOfStockLineItems?.map((lineItem: any) => {
      const variantId = lineItem.variantId
      const product = _productData[variantId]
      const productMetaData = product?.selectedVariant
      const slug = product?.productData?.slug

      return {
        ...lineItem,
        productMetaData,
        slug,
      }
    }) || []

  return {
    ...data,
    lineItems: lineItems,
    saveForLaterItems: saveForLaterlineItems,
    outOfStockLineItems: outOfStocklineItems,
  }
}

export function isB2BSelfServe(storeProgramId: string | number | null | undefined): boolean {
  return Boolean(storeProgramId) && isBrowser() && isB2BSelfServeContext()
}

export async function getDataWithProductAndInventory(data: Cart) {
  try {
    let productData: any
    let inventoryData: any
    let allVariantsInventory: Cart['allVariantsInventory']
    let allProductVariants: Cart['allProductVariants']

    if (isB2BSelfServe(data.storeProgramId)) {
      const result = await getProductDataForCartWithAllVariants(data)
      productData = result.productDataMap
      const allVariantIds = result.allVariantIds
      allProductVariants = result.allProductVariants
      const allVariantIdsString = allVariantIds.join(',')
      inventoryData = allVariantIdsString ? await getInventory({ variantIds: allVariantIdsString }) : {}
      allVariantsInventory = inventoryData
    } else {
      ;[productData, inventoryData] = await Promise.all([getProductDataForCart(data), getInventoryDataForCart(data)])
    }

    const inventoryDataWithCart = mergeInventoryDataInCart(data, inventoryData)
    let mergedData = mergeProductDataInCart(inventoryDataWithCart, productData)

    if (allVariantsInventory) {
      mergedData = { ...mergedData, allVariantsInventory }
    }
    if (allProductVariants) {
      mergedData = { ...mergedData, allProductVariants }
    }

    return mergedData
  } catch (error) {
    console.error(error)
    return data
  }
}

export const removeCartIdFromLocalStorage = () => {
  getStorage().removeItem(LOCAL_STORAGE_NEW_CART_ID_KEY, { isGlobal: false, removeAllLocalized: true })
}

/**
 * The region the persisted guest cart belongs to, read from the cached cart blob (the same slot the
 * reuse path reads its cart id from). Primary `cart.regionId`; falls back to deriving the region from
 * the required `cart.currencyCode` so carts cached before `regionId` was populated are still covered.
 * Returns null when no region can be determined.
 */
export const getStoredCartRegionId = (): number | null => {
  if (!isBrowser()) return null
  const cart = getStorage().getItemJSON<Cart>(LOCAL_STORAGE_CART_OBJ_KEY, { isGlobal: false })
  if (!cart) return null
  if (typeof cart.regionId === 'number' && Number.isFinite(cart.regionId)) {
    return cart.regionId
  }
  if (cart.currencyCode) {
    return CURRENCY_TO_REGION_ID[cart.currencyCode] ?? null
  }
  return null
}

/**
 * The region the cart-service will actually validate this request against: the region we are about to
 * SEND, read from `getLocalisationHeaders()` — the same function the HttpClient interceptor uses to
 * build the header — else the backend default (FALLBACK_REGION_ID) when no region is sent.
 *
 * Read the header rather than REGION_ID_COOKIE directly: the cookie is only one input, and the header
 * is dropped when the locale cookie is missing, when either cookie is missing, or in B2B mode. In all
 * of those cases the backend applies its US default, so a cookie-only read would predict the wrong
 * region and the guard would report a mismatch the backend would not have raised.
 */
export const getCurrentRegionId = (): number => {
  if (!isBrowser()) return FALLBACK_REGION_ID
  const sentRegionId = getLocalisationHeaders()[REGION_ID_HEADER]
  const parsed = sentRegionId ? Number(sentRegionId) : NaN
  return Number.isFinite(parsed) ? parsed : FALLBACK_REGION_ID
}

/**
 * True when the persisted guest cart's region matches the shopper's current region (safe to reuse).
 * Returns true when the cart's region is unknown so we never block reuse on missing data.
 */
export const storedCartRegionMatchesCurrent = (): boolean => {
  const storedRegionId = getStoredCartRegionId()
  if (storedRegionId === null) return true
  return storedRegionId === getCurrentRegionId()
}

export function setCartAuthCodeToLocalStorage(code: string) {
  getStorage().setItem(CART_AUTH_CODE_KEY, code, { isGlobal: false })
}
